import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const NavigationBreadcrumbs = ({ 
  customBreadcrumbs = null,
  showBackButton = true,
  onBackClick = null,
  className = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getDefaultBreadcrumbs = () => {
    const path = location?.pathname;
    const breadcrumbs = [
      { label: 'Dashboard', path: '/dashboard', icon: 'Home' }
    ];

    switch (path) {
      case '/contact-list':
        breadcrumbs?.push({ label: 'Contacts', path: '/contact-list', icon: 'Users' });
        break;
      case '/contact-details':
        breadcrumbs?.push(
          { label: 'Contacts', path: '/contact-list', icon: 'Users' },
          { label: 'Contact Details', path: '/contact-details', icon: 'User' }
        );
        break;
      case '/add-edit-contact':
        breadcrumbs?.push(
          { label: 'Contacts', path: '/contact-list', icon: 'Users' },
          { label: 'Add Contact', path: '/add-edit-contact', icon: 'UserPlus' }
        );
        break;
      case '/message-templates':
        breadcrumbs?.push({ label: 'Message Templates', path: '/message-templates', icon: 'MessageSquare' });
        break;
      case '/message-history':
        breadcrumbs?.push(
          { label: 'Messages', path: '/message-templates', icon: 'MessageSquare' },
          { label: 'Message History', path: '/message-history', icon: 'History' }
        );
        break;
      case '/random-contact':
        breadcrumbs?.push(
          { label: 'Messages', path: '/message-templates', icon: 'MessageSquare' },
          { label: 'Random Contact', path: '/random-contact', icon: 'Shuffle' }
        );
        break;
      case '/birthday-calendar':
        breadcrumbs?.push({ label: 'Birthday Calendar', path: '/birthday-calendar', icon: 'Calendar' });
        break;
      case '/settings':
        breadcrumbs?.push({ label: 'Settings', path: '/settings', icon: 'Settings' });
        break;
      default:
        return breadcrumbs;
    }

    return breadcrumbs;
  };

  const breadcrumbs = customBreadcrumbs || getDefaultBreadcrumbs();
  const currentPath = location?.pathname;

  // Don't show breadcrumbs on main dashboard or if only one item
  if (currentPath === '/dashboard' || breadcrumbs?.length <= 1) {
    return null;
  }

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const handleBreadcrumbClick = (path) => {
    if (path && path !== currentPath) {
      navigate(path);
    }
  };

  return (
    <nav className={`flex items-center space-x-2 py-3 px-4 lg:px-6 bg-background border-b border-border ${className}`}>
      {/* Back Button */}
      {showBackButton && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBackClick}
          className="mr-2 hover-lift"
        >
          <Icon name="ArrowLeft" size={20} />
        </Button>
      )}
      {/* Breadcrumb Items */}
      <div className="flex items-center space-x-2 min-w-0 flex-1">
        {breadcrumbs?.map((crumb, index) => {
          const isLast = index === breadcrumbs?.length - 1;
          const isClickable = crumb?.path && !isLast;

          return (
            <React.Fragment key={crumb?.path || index}>
              {index > 0 && (
                <Icon 
                  name="ChevronRight" 
                  size={16} 
                  className="text-text-secondary flex-shrink-0" 
                />
              )}
              <div className="flex items-center space-x-2 min-w-0">
                {crumb?.icon && (
                  <Icon 
                    name={crumb?.icon} 
                    size={16} 
                    className={`flex-shrink-0 ${
                      isLast ? 'text-primary' : 'text-text-secondary'
                    }`} 
                  />
                )}
                
                {isClickable ? (
                  <button
                    onClick={() => handleBreadcrumbClick(crumb?.path)}
                    className="text-text-secondary hover:text-primary transition-micro truncate font-medium"
                  >
                    {crumb?.label}
                  </button>
                ) : (
                  <span className={`truncate font-medium ${
                    isLast ? 'text-text-primary' : 'text-text-secondary'
                  }`}>
                    {crumb?.label}
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {/* Mobile Overflow Menu */}
      <div className="md:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {}}
        >
          <Icon name="MoreVertical" size={20} />
        </Button>
      </div>
    </nav>
  );
};

export default NavigationBreadcrumbs;